import React from 'react';
import ReactDOM from 'react-dom';
import { Rnd, type DraggableData, type RndDragEvent, type RndResizeCallback } from 'react-rnd';
import {
    getWindowViewport,
    PANEL_MARGIN,
    PANEL_MAX_HEIGHT,
    PANEL_MAX_WIDTH,
    PANEL_MIN_HEIGHT,
    PANEL_MIN_VISIBLE_SIZE,
    PANEL_MIN_WIDTH,
} from './panelSessionState';
import type { PanelRect, PanelSessionState } from './panelSessionState';
import type { PanelAttachment } from './panelSessionState';
import { useSessionPanelState } from './useSessionPanelState';
import InovxLoader from '@shared/global/InovxLoader';

export interface SessionPanelRenderContext {
    attachment: PanelSessionState['attachment'];
    closePanel: () => void;
    dragHandleClassName: string;
    panelHeight: number;
    panelState: PanelSessionState;
    toggleAttachment: () => void;
}

interface SessionPanelProps {
    attachedClassName: string;
    cancelSelector?: string;
    children: (context: SessionPanelRenderContext) => React.ReactNode;
    className: string;
    defaults: PanelSessionState;
    elementId: string;
    floatingClassName?: string;
    isOpen: boolean;
    keepMounted?: boolean;
    onAttachmentChange?: (attachment: PanelAttachment) => void;
    onOpenChange: (open: boolean) => void;
    panelId: string;
    panelRef?: React.RefObject<HTMLDivElement>;
    shouldRestoreOpen?: (panelState: PanelSessionState) => boolean;
}

interface SessionPanelLayoutContextValue {
    isOpen: boolean;
    panelHeight: number;
    panelRef: React.RefObject<HTMLDivElement>;
}

interface SessionPanelMeasuredContentProps {
    bottomPadding?: number;
    children: (height: number) => React.ReactNode;
    className?: string;
    fallbackOffset?: number;
    minHeight?: number;
}

interface SessionPanelToolbarProps {
    attachmentButtonId?: string;
    children?: React.ReactNode;
    closeButtonId?: string;
    closeClassName?: string;
    context: Pick<SessionPanelRenderContext, 'attachment' | 'closePanel' | 'dragHandleClassName' | 'toggleAttachment'>;
    itemClassName?: string;
    listClassName?: string;
    spacerClassName?: string;
    toolbarClassName?: string;
    toggleClassName?: string;
}

const DRAG_HANDLE_CLASS = 'session-panel-drag-handle';
const DEFAULT_CANCEL_SELECTOR = 'input, button, .e-control, .e-popup, .e-dlg-container';
const SessionPanelLayoutContext = React.createContext<SessionPanelLayoutContextValue | null>(null);

const getFloatingLimits = () => {
    const viewport = getWindowViewport();
    const availableWidth = Math.max(PANEL_MIN_VISIBLE_SIZE, viewport.width - (PANEL_MARGIN * 2));
    const availableHeight = Math.max(PANEL_MIN_VISIBLE_SIZE, viewport.height - (PANEL_MARGIN * 2));
    const minWidth = Math.min(PANEL_MIN_WIDTH, availableWidth);
    const minHeight = Math.min(PANEL_MIN_HEIGHT, availableHeight);

    return {
        minWidth,
        minHeight,
        maxWidth: availableWidth,
        maxHeight: availableHeight,
    };
};

const getElementRect = (element: HTMLElement, position: { x: number; y: number }): PanelRect => ({
    x: position.x,
    y: position.y,
    width: element.offsetWidth,
    height: element.offsetHeight,
});

export const SessionPanelMeasuredContent = (props: SessionPanelMeasuredContentProps) => {
    const context = React.useContext(SessionPanelLayoutContext);
    const panelRef = context?.panelRef;
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [contentTopOffset, setContentTopOffset] = React.useState(0);

    const measureContentTopOffset = React.useCallback(() => {
        const panelElement = panelRef?.current;
        const contentElement = contentRef.current;

        if (!panelElement || !contentElement) {
            return;
        }

        const nextOffset = Math.ceil(contentElement.getBoundingClientRect().top - panelElement.getBoundingClientRect().top);
        if (Number.isFinite(nextOffset) && nextOffset >= 0) {
            setContentTopOffset(currentOffset => currentOffset === nextOffset ? currentOffset : nextOffset);
        }
    }, [panelRef]);

    React.useLayoutEffect(() => {
        if (!context?.isOpen || typeof ResizeObserver === 'undefined') {
            return;
        }

        measureContentTopOffset();

        const panelElement = panelRef?.current;
        const contentElement = contentRef.current;
        const resizeObserver = new ResizeObserver(measureContentTopOffset);

        if (panelElement) {
            resizeObserver.observe(panelElement);
        }
        if (contentElement) {
            resizeObserver.observe(contentElement);
        }

        window.addEventListener('resize', measureContentTopOffset);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', measureContentTopOffset);
        };
    }, [context?.isOpen, measureContentTopOffset, panelRef, props.fallbackOffset]);

    const offset = contentTopOffset || props.fallbackOffset || 0;
    const availableHeight = context?.panelHeight
        ? Math.max(props.minHeight ?? 0, context.panelHeight - offset - (props.bottomPadding ?? 0))
        : 0;

    return (
        <div ref={contentRef} className={props.className}>
            {props.children(availableHeight)}
        </div>
    );
};

export const SessionPanelToolbar = (props: SessionPanelToolbarProps) => {
    const { attachment, closePanel, dragHandleClassName, toggleAttachment } = props.context;
    const itemClassName = props.itemClassName ?? 'session-panel-toolbar-item';

    return (
        <div className={`${props.toolbarClassName ?? 'session-panel-toolbar'} ${attachment === 'floating' ? dragHandleClassName : ''}`}>
            <ul className={props.listClassName ?? 'session-panel-toolbar-list'}>
                {props.children}
                <li className={props.spacerClassName ?? 'session-panel-toolbar-spacer'} aria-hidden="true"></li>
                <li
                    className={`${itemClassName} ${props.toggleClassName ?? 'panel-attachment-icon'} ${attachment}`}
                    id={props.attachmentButtonId}
                    title={InovxLoader.t(attachment === 'floating' ? 'common:panel.pin' : 'common:panel.detach')}
                    aria-label={InovxLoader.t(attachment === 'floating' ? 'common:panel.pin' : 'common:panel.detach')}
                    role="button"
                    onClick={toggleAttachment}
                ></li>
                <li
                    className={`${itemClassName} ${props.closeClassName ?? 'panel-close-icon'}`}
                    id={props.closeButtonId}
                    title={InovxLoader.t('common:panel.close')}
                    aria-label={InovxLoader.t('common:panel.close')}
                    role="button"
                    onClick={closePanel}
                ></li>
            </ul>
        </div>
    );
};

const SessionPanel = (props: SessionPanelProps) => {
    const [panelState, setPanelState] = useSessionPanelState(props.panelId, props.defaults);
    const [panelHeight, setPanelHeight] = React.useState(0);
    const [floatingDraft, setFloatingDraft] = React.useState<PanelRect | null>(null);
    const internalPanelRef = React.useRef<HTMLDivElement>(null);
    const panelRef = props.panelRef ?? internalPanelRef;
    const restoreAttemptedRef = React.useRef(false);

    React.useEffect(() => {
        if (!restoreAttemptedRef.current) {
            restoreAttemptedRef.current = true;
            const shouldRestoreOpen = props.shouldRestoreOpen?.(panelState) ?? true;
            if (panelState.isOpen && !props.isOpen && shouldRestoreOpen) {
                props.onOpenChange(true);
                return;
            }
        }

        if (panelState.isOpen === props.isOpen) {
            return;
        }

        setPanelState(currentState => ({
            ...currentState,
            isOpen: props.isOpen,
            activePanelId: props.panelId,
        }));
    }, [panelState, props.isOpen, props.onOpenChange, props.panelId, props.shouldRestoreOpen, setPanelState]);

    React.useEffect(() => {
        if (!props.isOpen || !panelRef.current || typeof ResizeObserver === 'undefined') {
            return;
        }

        const panelElement = panelRef.current;
        const resizeObserver = new ResizeObserver(() => {
            setPanelHeight(panelElement.clientHeight);
        });
        resizeObserver.observe(panelElement);
        setPanelHeight(panelElement.clientHeight);

        return () => resizeObserver.unobserve(panelElement);
    }, [props.isOpen, panelState.attachment]);

    React.useEffect(() => {
        if (!props.isOpen || panelState.attachment !== 'floating') {
            return;
        }

        setPanelHeight((floatingDraft ?? panelState.floating).height);
    }, [floatingDraft, panelState.attachment, panelState.floating, props.isOpen]);

    const closePanel = React.useCallback(() => {
        setPanelState(currentState => ({
            ...currentState,
            isOpen: false,
            activePanelId: props.panelId,
        }));
        props.onOpenChange(false);
    }, [props.onOpenChange, props.panelId, setPanelState]);

    const toggleAttachment = React.useCallback(() => {
        const nextAttachment = panelState.attachment === 'floating' ? 'pinned' : 'floating';
        setPanelState(currentState => ({
            ...currentState,
            isOpen: true,
            attachment: nextAttachment,
            activePanelId: props.panelId,
        }));
        props.onAttachmentChange?.(nextAttachment);
        window.setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    }, [panelState.attachment, props.onAttachmentChange, props.panelId, setPanelState]);

    const handlePinnedResizeStart = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startWidth = panelState.pinnedWidth;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const nextWidth = startWidth + (moveEvent.clientX - startX);
            setPanelState(currentState => ({
                ...currentState,
                pinnedWidth: nextWidth,
                activePanelId: props.panelId,
            }));
            window.dispatchEvent(new Event('resize'));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [panelState.pinnedWidth, props.panelId, setPanelState]);

    const handleFloatingDrag = React.useCallback((_event: RndDragEvent, data: DraggableData) => {
        setFloatingDraft(currentDraft => ({
            ...(currentDraft ?? panelState.floating),
            x: data.x,
            y: data.y,
        }));
    }, [panelState.floating]);

    const handleFloatingDragStop = React.useCallback((_event: RndDragEvent, data: DraggableData) => {
        const nextFloating = {
            ...panelState.floating,
            x: data.x,
            y: data.y,
        };
        setFloatingDraft(null);
        setPanelState(currentState => ({
            ...currentState,
            floating: nextFloating,
            activePanelId: props.panelId,
        }));
    }, [panelState.floating, props.panelId, setPanelState]);

    const handleFloatingResize = React.useCallback<RndResizeCallback>((_event, _direction, elementRef, _delta, position) => {
        setFloatingDraft(getElementRect(elementRef, position));
        window.dispatchEvent(new Event('resize'));
    }, []);

    const handleFloatingResizeStop = React.useCallback<RndResizeCallback>((_event, _direction, elementRef, _delta, position) => {
        const nextFloating = getElementRect(elementRef, position);
        setFloatingDraft(null);
        setPanelState(currentState => ({
            ...currentState,
            floating: nextFloating,
            activePanelId: props.panelId,
        }));
        window.dispatchEvent(new Event('resize'));
    }, [props.panelId, setPanelState]);

    const renderContext: SessionPanelRenderContext = {
        attachment: panelState.attachment,
        closePanel,
        dragHandleClassName: DRAG_HANDLE_CLASS,
        panelHeight,
        panelState,
        toggleAttachment,
    };

    const content = (props.isOpen || props.keepMounted) ? props.children(renderContext) : null;
    const layoutContext = React.useMemo(() => ({
        isOpen: props.isOpen,
        panelHeight,
        panelRef,
    }), [panelHeight, panelRef, props.isOpen]);
    const panelStyle = {
        '--session-panel-width': `${panelState.pinnedWidth}px`,
    } as React.CSSProperties;

    const attachedPlaceholder = (
        <div
            className="session-panel-attached-placeholder"
            data-session-panel-placeholder={props.panelId}
            aria-hidden="true"
        ></div>
    );

    if (props.isOpen && panelState.attachment === 'floating' && typeof document !== 'undefined') {
        const floatingLimits = getFloatingLimits();
        const floatingRect = floatingDraft ?? panelState.floating;

        return (
            <>
                {attachedPlaceholder}
                {ReactDOM.createPortal(
                    <Rnd
                        bounds="window"
                        className="session-panel-floating-frame"
                        dragHandleClassName={DRAG_HANDLE_CLASS}
                        cancel={props.cancelSelector ?? DEFAULT_CANCEL_SELECTOR}
                        minWidth={floatingLimits.minWidth}
                        minHeight={floatingLimits.minHeight}
                        maxWidth={floatingLimits.maxWidth}
                        maxHeight={floatingLimits.maxHeight}
                        size={{ width: floatingRect.width, height: floatingRect.height }}
                        position={{ x: floatingRect.x, y: floatingRect.y }}
                        onDrag={handleFloatingDrag}
                        onDragStop={handleFloatingDragStop}
                        onResize={handleFloatingResize}
                        onResizeStop={handleFloatingResizeStop}
                    >
                        <div
                            ref={panelRef}
                            className={`${props.className} ${props.floatingClassName ?? ''}`}
                            data-session-panel-id={props.panelId}
                            id={props.elementId}
                            style={panelStyle}
                        >
                            <SessionPanelLayoutContext.Provider value={layoutContext}>
                                {content}
                            </SessionPanelLayoutContext.Provider>
                        </div>
                    </Rnd>,
                    document.body
                )}
            </>
        );
    }

    return (
        <>
            <div
                ref={panelRef}
                className={`${props.className} ${props.attachedClassName}`}
                data-session-panel-id={props.panelId}
                id={props.elementId}
                style={panelStyle}
            >
                <SessionPanelLayoutContext.Provider value={layoutContext}>
                    {content}
                </SessionPanelLayoutContext.Provider>
                {props.isOpen && panelState.attachment === 'pinned' && (
                    <div
                        className="session-panel-pinned-resize-handle"
                        title={InovxLoader.t('common:panel.resize')}
                        onMouseDown={handlePinnedResizeStart}
                    ></div>
                )}
            </div>
        </>
    );
};

export default SessionPanel;
